'use client';

/**
 * Obeya Kanban - Workflow Stage Visualization
 *
 * @component_role Kanban board for RFQ/PO workflow stages
 * @business_rule Same data as ObeyaWidget graph, different lens:
 *   - Graph view: "How are things connected?"
 *   - Kanban view: "Where are things in the workflow?"
 * @user_outcome See RFQs and POs organized by stage, with RAG indicators.
 *   Drag cards between columns (future: write back to Odoo).
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain obeya
 */

import { useState, useMemo, useEffect, useRef, useLayoutEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ChevronRight,
  FileText,
  Layers,
  ShoppingCart,
  GripVertical,
  MessageSquare,
  Link2,
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { text } from '@/lib/design/tokens.x';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useWorkspaceLabels } from '@/lib/stores/total-matrix-store';
import type { RAGStatus, RAGNodeStatus, VendorResponseSummary } from '@/lib/api/types';

// Column definitions
type ColumnId = 'draft' | 'sent' | 'responded' | 'complete';

interface ColumnDef {
  id: ColumnId;
  title: string;
  statuses: string[];
}

const COLUMNS: ColumnDef[] = [
  { id: 'draft', title: 'Draft', statuses: ['draft', 'new', 'pending', 'unknown'] },
  { id: 'sent', title: 'Sent', statuses: ['sent', 'in_progress', 'rfq_sent'] },
  { id: 'responded', title: 'Responded', statuses: ['responded', 'quoted', 'received', 'done'] },
  { id: 'complete', title: 'Complete', statuses: ['complete', 'awarded', 'cancelled', 'confirmed', 'purchase'] },
];

interface ObeyaKanbanProps {
  projectId: string;
  rfqs: RAGNodeStatus[];
  pos: RAGNodeStatus[];
}

interface KanbanCard extends RAGNodeStatus {
  columnId: ColumnId;
}

/** A column row is either a single RFQ/PO card, or a "tender slice" —
 *  the subset of an x_rfq_group_id's siblings that share this column.
 *  Singletons + POs + legacy ungrouped RFQs render as plain cards. */
type RenderItem =
  | { kind: 'card'; card: KanbanCard }
  | {
      kind: 'tender';
      /** Stable id for SortableContext / drag — unique across columns. */
      tenderId: string;
      rfqGroup: string;
      /** Display label — prefers ``x_rfq_group_name`` (set at mint), falls
       *  back to the trailing slug of ``x_rfq_group_id`` for legacy rows. */
      tenderLabel: string;
      columnId: ColumnId;
      /** Sibling RFQs in this state-bucket. */
      cards: KanbanCard[];
      /** Sibling count across ALL columns — surfaces the "M of N" hint. */
      siblingCountTotal: number;
    };

function rfqGroupOf(card: KanbanCard): string | undefined {
  if (card.entity_type !== 'RFQ') return undefined;
  const rg = card.properties?.x_rfq_group_id;
  return typeof rg === 'string' && rg.length > 0 ? rg : undefined;
}

function rfqGroupNameOf(card: KanbanCard): string | undefined {
  const n = card.properties?.x_rfq_group_name;
  return typeof n === 'string' && n.length > 0 ? n : undefined;
}

/** CostPlan this RFQ is currently linked to via mcp_rfq_link (plan level
 *  only — see klyde_edge/docs/mcp_engine_concept.md). Read straight out
 *  of properties, same pattern as rfqGroupNameOf — no second query. */
function linkedPlanNameOf(card: KanbanCard): string | undefined {
  if (card.entity_type !== 'RFQ') return undefined;
  const n = card.properties?.linked_plan_name;
  return typeof n === 'string' && n.length > 0 ? n : undefined;
}

/** Map an RFQ-state value (from pipeline-stage focus) to the column
 * that displays items in that state. Returns null if no column claims it. */
function findColumnForState(state: string | null | undefined): ColumnId | null {
  if (!state) return null;
  for (const col of COLUMNS) {
    if (col.statuses.includes(state)) return col.id;
  }
  return null;
}

function getColumnForItem(item: RAGNodeStatus): ColumnId {
  const status = item.properties?.status as string | undefined;
  const responseCount = item.properties?.response_count as number | undefined;

  // RFQs with vendor responses go to "responded" column
  if (item.entity_type === 'RFQ' && responseCount && responseCount > 0) {
    return 'responded';
  }

  if (!status) return 'draft';
  const normalized = status.toLowerCase();
  for (const col of COLUMNS) {
    if (col.statuses.includes(normalized)) return col.id;
  }
  return 'draft'; // Default
}

export function ObeyaKanban({ projectId, rfqs, pos }: ObeyaKanbanProps) {
  const selectNode = useGraphSelectionStore((state) => state.selectNode);
  // Closed-vocab display map for the column titles (Draft/Sent/…).
  // Fall-through lookup against the canonical Odoo state value
  // (column.id matches): raw title renders when the map doesn't
  // carry an entry. Per-card RAG labels translated inside the
  // KanbanCardComponent (its own useWorkspaceLabels read). See
  // PR #1 backend / `klyde_edge/ui_config/labels/workspace.py`.
  const { state: stateLabels } = useWorkspaceLabels();

  // Pipeline-stage focus deep-link from the home dashboard. When set,
  // scroll the matching column into view, briefly highlight it, then
  // clear the focus signal (one-shot — refresh shouldn't keep firing).
  const pipelineStageFocus = useLayoutStore((s) => s.pipelineStageFocus);
  const clearPipelineStageFocus = useLayoutStore((s) => s.clearPipelineStageFocus);
  const focusedColumnId = findColumnForState(pipelineStageFocus);
  const columnRefs = useRef<Record<ColumnId, HTMLDivElement | null>>({
    draft: null, sent: null, responded: null, complete: null,
  });
  const [highlightedColumn, setHighlightedColumn] = useState<ColumnId | null>(null);

  useLayoutEffect(() => {
    if (!focusedColumnId) return;
    const el = columnRefs.current[focusedColumnId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      setHighlightedColumn(focusedColumnId);
      const t = setTimeout(() => setHighlightedColumn(null), 2500);
      return () => clearTimeout(t);
    }
  }, [focusedColumnId]);

  useEffect(() => {
    // One-shot — consume after applying so a refresh / navigation doesn't
    // re-trigger. Kept separate from the layout-effect so the scroll fires
    // before the focus is cleared.
    if (focusedColumnId) clearPipelineStageFocus();
  }, [focusedColumnId, clearPipelineStageFocus]);

  // Handle card click - update Copilot selection
  const handleCardClick = (card: KanbanCard) => {
    const entityType = card.entity_type === 'RFQ' ? 'rfq' : 'po';
    selectNode({
      id: card.semantic_id,
      label: card.name || card.semantic_id,
      type: entityType,
      status: card.properties?.status as string | undefined,
      properties: card.properties,
    }, projectId);
  };

  // Handle vendor response click - select in Copilot
  const handleResponseClick = (response: VendorResponseSummary) => {
    selectNode({
      id: response.semantic_id,
      label: response.vendor_name || response.semantic_id,
      type: 'vendor_response',
      properties: {
        total_amount: response.total_amount,
        rag_status: response.rag_status,
        rag_label: response.rag_label,
      },
    }, projectId);
  };

  // Combine and categorize cards
  const initialCards = useMemo(() => {
    const allItems = [
      ...rfqs.map((item) => ({ ...item, entityType: 'RFQ' as const })),
      ...pos.map((item) => ({ ...item, entityType: 'PurchaseOrder' as const })),
    ];

    return allItems.map((item) => ({
      ...item,
      columnId: getColumnForItem(item),
    }));
  }, [rfqs, pos]);

  const [cards, setCards] = useState<KanbanCard[]>(initialCards);
  const [activeCard, setActiveCard] = useState<KanbanCard | null>(null);

  // Sync cards when props change (e.g., new project selected)
  useEffect(() => {
    setCards(initialCards);
  }, [initialCards]);

  // Group cards by column, folding sibling RFQs (same x_rfq_group_id) into a
  // per-column "tender slice" card. A tender with vendors split across
  // states renders as one slice card per state — keeps the laggard
  // visible in its own column instead of hiding it behind a popover.
  const cardsByColumn = useMemo(() => {
    // Pass 1: total siblings per tender across ALL columns. Drives the
    // "2 of 3 vendors" hint when a tender's vendors are split.
    const tenderTotal: Record<string, number> = {};
    for (const c of cards) {
      const rg = rfqGroupOf(c);
      if (rg) tenderTotal[rg] = (tenderTotal[rg] ?? 0) + 1;
    }

    const grouped: Record<ColumnId, RenderItem[]> = {
      draft: [], sent: [], responded: [], complete: [],
    };
    // (x_rfq_group_id, columnId) → index of the tender slice already added
    // to grouped[columnId], so subsequent siblings append to the slice
    // rather than emitting a new one.
    const sliceIndex: Record<string, number> = {};

    for (const card of cards) {
      const col = card.columnId;
      const rg = rfqGroupOf(card);
      // Singleton tenders + POs + legacy ungrouped: render as plain card
      // (preserves the existing single-RFQ kanban behaviour).
      if (!rg || (tenderTotal[rg] ?? 0) <= 1) {
        grouped[col].push({ kind: 'card', card });
        continue;
      }

      const key = `${rg}::${col}`;
      if (sliceIndex[key] === undefined) {
        sliceIndex[key] = grouped[col].length;
        const tenderName = rfqGroupNameOf(card);
        grouped[col].push({
          kind: 'tender',
          tenderId: key,
          rfqGroup: rg,
          // Prefer the human-readable name; fall back to the slug.
          tenderLabel: tenderName ?? `Tender ${rg.split('/').slice(-1)[0] ?? rg}`,
          columnId: col,
          cards: [],
          siblingCountTotal: tenderTotal[rg],
        });
      }
      const slice = grouped[col][sliceIndex[key]] as Extract<RenderItem, { kind: 'tender' }>;
      slice.cards.push(card);
    }
    return grouped;
  }, [cards]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = (event: DragStartEvent) => {
    const card = cards.find((c) => c.semantic_id === event.active.id);
    if (card) setActiveCard(card);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveCard(null);

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Tender slices use composite ids ("RFG/.../KEY::columnId") — they
    // are intentionally non-draggable for now. The buyer drags individual
    // vendor RFQs, not the whole tender slice. Skip without writeback.
    if (activeId.includes('::')) return;

    // Check if dropped on a column
    const targetColumn = COLUMNS.find((col) => col.id === overId);
    if (targetColumn) {
      setCards((prev) =>
        prev.map((card) =>
          card.semantic_id === activeId
            ? { ...card, columnId: targetColumn.id }
            : card
        )
      );
      // TODO: Write back to Odoo when drag completes
      console.log(`[Obeya] Would update ${activeId} to stage: ${targetColumn.id}`);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="h-full flex gap-3 p-4 overflow-x-auto">
        {COLUMNS.map((column) => (
          <KanbanColumn
            key={column.id}
            id={column.id}
            title={stateLabels[column.id] ?? column.title}
            items={cardsByColumn[column.id]}
            onCardClick={handleCardClick}
            onResponseClick={handleResponseClick}
            highlight={highlightedColumn === column.id}
            outerRef={(el) => { columnRefs.current[column.id] = el; }}
          />
        ))}
      </div>

      <DragOverlay>
        {activeCard && <KanbanCardComponent card={activeCard} isDragging />}
      </DragOverlay>
    </DndContext>
  );
}

interface KanbanColumnProps {
  id: ColumnId;
  title: string;
  items: RenderItem[];
  onCardClick: (card: KanbanCard) => void;
  onResponseClick: (response: VendorResponseSummary) => void;
  highlight?: boolean;
  outerRef?: (el: HTMLDivElement | null) => void;
}

function KanbanColumn({
  id, title, items, onCardClick, onResponseClick, highlight, outerRef,
}: KanbanColumnProps) {
  const { setNodeRef, isOver } = useSortable({
    id,
    data: { type: 'column' },
  });

  // Compose the dnd-kit ref with our outer scroll-into-view ref.
  const composedRef = (el: HTMLDivElement | null) => {
    setNodeRef(el);
    outerRef?.(el);
  };

  // Total card count for the column header — counts RFQs/POs, not slices.
  // A tender slice with 3 vendors counts as 3, so the column tally still
  // matches "items in this state."
  const totalCards = items.reduce(
    (n, it) => n + (it.kind === 'card' ? 1 : it.cards.length),
    0,
  );

  // Count RAG statuses (flatten tender slices to their child RFQs).
  const ragCounts = items.reduce(
    (acc, it) => {
      const cards = it.kind === 'card' ? [it.card] : it.cards;
      for (const c of cards) acc[c.rag_status] = (acc[c.rag_status] || 0) + 1;
      return acc;
    },
    {} as Record<RAGStatus, number>
  );

  return (
    <div
      ref={composedRef}
      className={cn(
        'flex flex-col w-72 min-w-72 bg-muted/30 rounded-lg border border-border/50 transition-shadow duration-300',
        isOver && 'ring-2 ring-primary/50',
        highlight && 'ring-2 ring-primary shadow-lg shadow-primary/20'
      )}
    >
      {/* Column Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/50">
        <div className="flex items-center gap-2">
          <span className={text.title}>{title}</span>
          <span className={cn(text.muted, 'bg-muted px-1.5 py-0.5 rounded')}>
            {totalCards}
          </span>
        </div>
        {/* RAG mini-indicators */}
        <div className="flex items-center gap-1">
          {ragCounts.red > 0 && (
            <span className={cn(text.muted, 'flex items-center gap-0.5 text-destructive')}>
              <AlertCircle className="w-3 h-3" />
              {ragCounts.red}
            </span>
          )}
          {ragCounts.amber > 0 && (
            <span className={cn(text.muted, 'flex items-center gap-0.5 text-warning')}>
              <AlertTriangle className="w-3 h-3" />
              {ragCounts.amber}
            </span>
          )}
        </div>
      </div>

      {/* Cards */}
      <div className="flex-1 p-2 space-y-2 overflow-y-auto">
        <SortableContext
          items={items.map((it) => it.kind === 'card' ? it.card.semantic_id : it.tenderId)}
          strategy={verticalListSortingStrategy}
        >
          {items.map((it) =>
            it.kind === 'card' ? (
              <SortableCard
                key={it.card.semantic_id}
                card={it.card}
                onClick={() => onCardClick(it.card)}
                onResponseClick={onResponseClick}
              />
            ) : (
              <TenderSliceCard
                key={it.tenderId}
                slice={it}
                onCardClick={onCardClick}
                onResponseClick={onResponseClick}
              />
            )
          )}
        </SortableContext>

        {items.length === 0 && (
          <div className={cn(text.muted, 'text-center py-8')}>
            No items
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Tender Slice — sibling RFQs in one column folded into one card
// ============================================================

interface TenderSliceCardProps {
  slice: Extract<RenderItem, { kind: 'tender' }>;
  onCardClick: (card: KanbanCard) => void;
  onResponseClick: (response: VendorResponseSummary) => void;
}

function TenderSliceCard({ slice, onCardClick, onResponseClick }: TenderSliceCardProps) {
  const [expanded, setExpanded] = useState(false);
  // Tender card RAG = worst across siblings in this slice. Red if any
  // are red, else amber if any amber, else green/unknown.
  const sliceRag: RAGStatus = slice.cards.some(c => c.rag_status === 'red')
    ? 'red'
    : slice.cards.some(c => c.rag_status === 'amber')
      ? 'amber'
      : (slice.cards[0]?.rag_status ?? 'unknown');

  const ragColors: Record<RAGStatus, string> = {
    red: 'border-l-destructive bg-destructive/5',
    amber: 'border-l-warning bg-warning/5',
    green: 'border-l-success',
    revised: 'border-l-info bg-info/5',
    unknown: 'border-l-muted-foreground',
  };

  // "3 vendors" when the whole tender is in this column,
  // "2 of 3 vendors" when split across columns.
  const sizeLabel = slice.cards.length === slice.siblingCountTotal
    ? `${slice.cards.length} vendor${slice.cards.length === 1 ? '' : 's'}`
    : `${slice.cards.length} of ${slice.siblingCountTotal} vendors`;

  // Select the whole tender by anchoring on its first sibling —
  // rfq_health / tender_fanout resolve any sibling to the whole
  // x_rfq_group_id, so sel:rfq on a member surfaces tender-level
  // health for the group. Reuses onCardClick (→ handleCardClick →
  // selectNode type:'rfq'). Also expands so the siblings are visible.
  const handleSelectTender = () => {
    const anchor = slice.cards[0];
    if (anchor) onCardClick(anchor);
    setExpanded(true);
  };

  return (
    <div className={cn(
      'bg-background border border-border/50 rounded-md',
      'border-l-4',
      ragColors[sliceRag],
    )}>
      {/* Header: the chevron toggles expand; the label area selects the
          tender. A <button> can't nest inside a <button>, so the header
          is a <div> with two distinct click targets. */}
      <div className="w-full p-2 flex items-start gap-2">
        <button
          onClick={() => setExpanded(!expanded)}
          aria-label={expanded ? 'Collapse tender' : 'Expand tender'}
          className="shrink-0"
        >
          <ChevronRight className={cn(
            'w-4 h-4 mt-0.5 text-muted-foreground transition-transform',
            expanded && 'rotate-90',
          )} />
        </button>
        <button
          onClick={handleSelectTender}
          className="flex-1 min-w-0 flex items-start gap-2 text-left"
        >
          <Layers className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <div className={cn(text.title, 'truncate')}>
              {slice.tenderLabel}
            </div>
            <div className={cn(text.muted, 'mt-0.5')}>
              {sizeLabel}
            </div>
          </div>
        </button>
      </div>
      {expanded && (
        <div className="border-t border-border/30 p-1.5 space-y-1.5">
          {slice.cards.map((card) => (
            <KanbanCardComponent
              key={card.semantic_id}
              card={card}
              onClick={() => onCardClick(card)}
              onResponseClick={onResponseClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface SortableCardProps {
  card: KanbanCard;
  onClick: () => void;
  onResponseClick: (response: VendorResponseSummary) => void;
}

function SortableCard({ card, onClick, onResponseClick }: SortableCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: card.semantic_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <KanbanCardComponent
        card={card}
        isDragging={isDragging}
        listeners={listeners}
        onClick={onClick}
        onResponseClick={onResponseClick}
      />
    </div>
  );
}

interface KanbanCardComponentProps {
  card: KanbanCard;
  isDragging?: boolean;
  listeners?: ReturnType<typeof useSortable>['listeners'];
  onClick?: () => void;
  onResponseClick?: (response: VendorResponseSummary) => void;
}

function KanbanCardComponent({ card, isDragging, listeners, onClick, onResponseClick }: KanbanCardComponentProps) {
  const selectedNode = useGraphSelectionStore((state) => state.selectedNode);
  const isSelected = selectedNode?.id === card.semantic_id;
  const isRFQ = card.entity_type === 'RFQ';
  const Icon = isRFQ ? FileText : ShoppingCart;
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Localized fall-through for the per-card RAG label
  // (e.g. "RFQ: On schedule" → "RFQ: 进行正常"). Raw label renders
  // when the map doesn't carry the entry.
  const { rag_label: ragLabels } = useWorkspaceLabels();

  // One-shot RFQ-focus signal — deep-link from the home recent-activity
  // card. When rfqFocus matches this card's sid, scroll it into view,
  // highlight briefly, and clear the signal (same pattern as
  // pipelineStageFocus on the kanban columns).
  const rfqFocus = useLayoutStore((s) => s.rfqFocus);
  const clearRfqFocus = useLayoutStore((s) => s.clearRfqFocus);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  useLayoutEffect(() => {
    if (rfqFocus !== card.semantic_id) return;
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    setIsFocused(true);
    const t = setTimeout(() => setIsFocused(false), 2500);
    return () => clearTimeout(t);
  }, [rfqFocus, card.semantic_id]);
  useEffect(() => {
    if (rfqFocus === card.semantic_id) clearRfqFocus();
  }, [rfqFocus, card.semantic_id, clearRfqFocus]);

  // Get vendor responses from the card (only for RFQs)
  const vendorResponses = card.vendor_responses || [];
  const hasResponses = vendorResponses.length > 0;
  const linkedPlanName = linkedPlanNameOf(card);

  // Delayed hover to prevent flicker
  const handleMouseEnter = () => {
    if (hasResponses) {
      hoverTimeoutRef.current = setTimeout(() => setIsHovered(true), 150);
    }
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovered(false);
  };

  const ragColors = {
    red: 'border-l-destructive bg-destructive/5',
    amber: 'border-l-warning bg-warning/5',
    green: 'border-l-success',
    revised: 'border-l-info bg-info/5',
    unknown: 'border-l-muted-foreground',
  };

  const ragIcons = {
    red: <AlertCircle className="w-3 h-3 text-destructive" />,
    amber: <AlertTriangle className="w-3 h-3 text-warning" />,
    green: <CheckCircle className="w-3 h-3 text-success" />,
    unknown: null,
  };

  return (
    <div
      ref={cardRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Main RFQ/PO card */}
      <div
        onClick={onClick}
        className={cn(
          'bg-background border border-border/50 rounded-md p-2 cursor-pointer',
          'border-l-4 transition-all relative',
          ragColors[card.rag_status],
          isSelected && 'ring-2 ring-primary',
          isFocused && 'ring-2 ring-primary ring-offset-2 shadow-lg',
          isDragging && 'shadow-lg opacity-90 cursor-grabbing',
          isHovered && hasResponses && 'z-20'
        )}
      >
        {/* Card Header */}
        <div className="flex items-start gap-2">
          <div
            {...listeners}
            className="mt-0.5 text-muted-foreground hover:text-foreground cursor-grab"
          >
            <GripVertical className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <span className={cn(text.title, 'truncate')}>
                {card.name || card.semantic_id}
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-1">
              {ragIcons[card.rag_status]}
              <span className={cn(text.muted, 'truncate')}>
                {card.rag_label ? (ragLabels[card.rag_label] ?? card.rag_label) : ''}
              </span>
            </div>
          </div>
        </div>

        {/* Card Footer */}
        <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-border/30">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className={text.muted}>
              {isRFQ ? 'RFQ' : 'PO'}
            </span>
            {linkedPlanName && (
              <span
                title={`Linked to ${linkedPlanName}`}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-xs shrink-0 max-w-24 min-w-0"
              >
                <Link2 className="w-2.5 h-2.5 shrink-0" />
                <span className="truncate">{linkedPlanName}</span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isRFQ && hasResponses && (
              <span className={cn(text.muted, 'text-success flex items-center gap-0.5')}>
                <MessageSquare className="w-2.5 h-2.5" />
                {vendorResponses.length}
              </span>
            )}
            <span className={text.muted}>
              {card.properties?.status as string || 'unknown'}
            </span>
          </div>
        </div>
      </div>

      {/* Vendor response cards - expand below on hover */}
      {hasResponses && isHovered && (
        <div className="mt-1 space-y-1">
          {vendorResponses.map((response, index) => (
            <VendorResponseCard
              key={response.semantic_id}
              response={response}
              index={index}
              onClick={() => onResponseClick?.(response)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Vendor Response Card (for stacking)
// ============================================================

interface VendorResponseCardProps {
  response: VendorResponseSummary;
  index: number;
  onClick: () => void;
}

function VendorResponseCard({ response, index, onClick }: VendorResponseCardProps) {
  const selectedNode = useGraphSelectionStore((state) => state.selectedNode);
  const isSelected = selectedNode?.id === response.semantic_id;

  const ragColors = {
    red: 'border-l-destructive bg-destructive/5',
    amber: 'border-l-warning bg-warning/5',
    green: 'border-l-success bg-success/5',
    unknown: 'border-l-muted-foreground bg-muted/30',
  };

  const ragIcons = {
    red: <AlertCircle className="w-2.5 h-2.5 text-destructive" />,
    amber: <AlertTriangle className="w-2.5 h-2.5 text-warning" />,
    green: <CheckCircle className="w-2.5 h-2.5 text-success" />,
    unknown: null,
  };

  // Format currency
  const formatAmount = (amount: number | null) => {
    if (amount === null) return null;
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        'bg-background border border-border/50 rounded-md p-1.5 cursor-pointer',
        'border-l-4 shadow-sm hover:shadow-md transition-all',
        'animate-in slide-in-from-top-1 fade-in duration-150',
        ragColors[response.rag_status],
        isSelected && 'ring-2 ring-primary'
      )}
      style={{
        animationDelay: `${index * 50}ms`,
        animationFillMode: 'backwards',
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <MessageSquare className="w-3 h-3 text-muted-foreground shrink-0" />
          <span className={cn(text.muted, 'font-medium truncate')}>
            {response.vendor_name || 'Unknown'}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {ragIcons[response.rag_status]}
          {response.total_amount !== null && (
            <span className={text.muted}>
              {formatAmount(response.total_amount)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
